import { Stripe, StripeElements } from '@stripe/stripe-js';
import { PaymentDtoLevelEnum, PaymentMethodDto, StripePaymentSource, SubscribeDtoPeriodEnum } from 'api/generated';
import httpClient, { RequestConfig } from 'api/https-client';
import { AppConfig } from 'config';
import { levelMapping } from 'constant';
import { routes } from 'constant/routes';
import { useGoogleAnalytics } from 'hooks';
import { FormEvent, useRef, useState } from 'react';
import { store } from 'store';
import { setErrorStripe } from 'utils';
import { trackAmplitudeEvent } from 'utils/trackAmplitudeEvent';

interface FormProps {
  elements: StripeElements | null;
  stripe: Stripe | null;
  clientSecret: string | undefined;
  courseId?: string;
  promocode: string | '';
  level?: PaymentDtoLevelEnum;
  source?: StripePaymentSource;
  period?: SubscribeDtoPeriodEnum;
  amount?: number;
  courseName?: string;
  isTrial?: boolean;
  isPurchase?: boolean;
  setTransactionId: any;
  isActivate: boolean | undefined;
}

interface CustomFormEvent<T = Element> extends FormEvent<T> {
  elementType?: string;
}

interface ConfirmStripeArgs {
  stripe: Stripe;
  clientSecret: string;
  elements?: StripeElements;
  paymentMethodId?: string;
}

export const useStripeSubscribeOrPurchase = ({
  elements,
  stripe,
  clientSecret,
  courseId,
  promocode,
  level,
  source,
  period,
  amount,
  courseName,
  isTrial,
  isPurchase,
  setTransactionId,
  isActivate,
}: FormProps) => {
  const {
    getEventSubscriptionPayPressedGA,
    getEventSubscribeGA,
    getEventPurchaseGA,
    getEventSubscriptionFromTrialVersionGA,
    getEventPurchaseFromTrialVersionGA,
  } = useGoogleAnalytics();

  const [isSuccess, setIsSuccess] = useState(false);

  const [isLoading, setIsLoading] = useState(false);
  const isSubmittingRef = useRef(false);

  const [errorMessage, setErrorMessage] = useState<string | null>(null);

  const returnUrl = new URL(routes.addNewCourse, AppConfig.BASE_API_URL || '');

  const handleSubmit = async (e?: CustomFormEvent<HTMLFormElement>, paymentMethod?: PaymentMethodDto) => {
    if (isSubmittingRef.current) {
      return;
    }

    if (e?.elementType !== 'expressCheckout') {
      e?.preventDefault();
    }

    isSubmittingRef.current = true;
    setIsLoading(true);
    getEventSubscriptionPayPressedGA();

    if (!stripe || !courseId || !level || !source || !period) {
      setIsLoading(false);
      isSubmittingRef.current = false;
      return;
    }

    try {
      if (paymentMethod && clientSecret) {
        await confirmStripe({
          stripe,
          elements: undefined,
          clientSecret,
          paymentMethodId: paymentMethod.id,
        });
      }

      if (!paymentMethod && elements) {
        if (!clientSecret) {
          throw new Error('Missing Stripe client secret for payment confirmation');
        }

        await confirmStripe({ stripe, elements, clientSecret, paymentMethodId: undefined });
      }

      setIsLoading(false);
    } catch (error: any) {
      setErrorStripe({
        error,
        setErrorMessage,
      });

      setIsSuccess(false);
      setIsLoading(false);
    } finally {
      isSubmittingRef.current = false;
    }
  };

  const confirmStripe = async ({ stripe, elements, clientSecret, paymentMethodId }: ConfirmStripeArgs) => {
    const confirmParams = {
      payment_method: paymentMethodId,
      return_url: returnUrl.href,
      mandate_data: paymentMethodId ? ({} as Record<string, never>) : undefined,
    };

    try {
      if (clientSecret) {
        const paymentIntent = (await stripe.retrievePaymentIntent(clientSecret)).paymentIntent;
        setTransactionId(paymentIntent?.id);
      }
    } catch (error) {
      console.log(error);
    }

    try {
      elements && (await elements.submit());

      const res = await stripe.confirmPayment({
        elements,
        clientSecret,
        redirect: 'if_required',
        confirmParams,
      });

      if (!res.error && !res.paymentIntent.last_payment_error) {
        if (courseId && res.paymentIntent?.id) {
          const accessToken = store.getState().auth.accessToken;

          try {
            if (accessToken) {
              await httpClient.post(
                '/payments/reconcile-payment-intent',
                {
                  courseId,
                  paymentIntentId: res.paymentIntent.id,
                },
                { useToken: true } as RequestConfig,
              );
            }
          } catch (reconcileError) {
            console.warn('Payment reconcile failed:', reconcileError);
          }
        }

        setIsSuccess(true);

        if (isPurchase && isActivate && level)
          trackAmplitudeEvent(`'Lifetime ${levelMapping[level]} Subscription Paid`);

        if (!isPurchase && isActivate && level) trackAmplitudeEvent(`${levelMapping[level]} Subscription Paid`);
      } else {
        setErrorMessage('Something went wrong');
      }

      if (isTrial && courseId && amount && level && period) {
        const eventParams = {
          amount,
          courseId,
          subscribeType: level,
          courseName,
          promoCode: promocode,
          term: period,
        };

        const eventFunction = isPurchase ? getEventPurchaseGA : getEventSubscribeGA;

        if (isPurchase && isActivate) {
          getEventPurchaseFromTrialVersionGA();
          eventFunction(eventParams);
        }

        if (!isPurchase && isActivate) {
          getEventSubscriptionFromTrialVersionGA();
          eventFunction(eventParams);
        }
      }
    } catch (error: any) {
      setErrorMessage('Something went wrong');
    }
  };
  return {
    handleSubmit,
    isLoading,
    errorMessage,
    isSuccess,
  };
};
